import { useEffect } from "react";
import { buildBreadcrumbSchema } from "./breadcrumbSchema";

/* <Seo> – drop-in component that updates document head per route.
   Sets:  document.title, meta description, canonical link, Open Graph
   tags, Twitter Card tags. Also auto-emits a BreadcrumbList JSON-LD
   block matching the current route (when one is defined in
   breadcrumbSchema.js). Returns null. Safe to drop anywhere inside
   a route component's JSX. */

const SITE_ORIGIN =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_SITE_ORIGIN) ||
  "https://www.british-sign.co.uk";
const DEFAULT_OG_IMAGE = "/images/og-default.jpg";

const upsertMeta = (selector, attrs) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
};

const upsertLink = (rel, href) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const absolutise = (urlOrPath) => {
  if (!urlOrPath) return SITE_ORIGIN + DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  return SITE_ORIGIN + (urlOrPath.startsWith("/") ? urlOrPath : "/" + urlOrPath);
};

const Seo = ({ title, description, path, image, type = "website", noindex = false }) => {
  useEffect(() => {
    const resolvedPath =
      path ||
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonical = SITE_ORIGIN + resolvedPath;
    const ogImage = absolutise(image);

    if (title) document.title = title;
    if (description)
      upsertMeta('meta[name="description"]', {
        name: "description",
        content: description,
      });

    // Own the robots meta on every routed page. index.html ships a default
    // `index, follow`, but utility routes (404, admin, embed widgets, print
    // artifacts) must not be indexed. Writing it here (rather than relying on
    // unmount restore) means SPA navigation always lands on the right value:
    // a noindex page sets noindex, the next page resets to index, follow.
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex
        ? "noindex, nofollow, noarchive, nosnippet"
        : "index, follow",
    });

    upsertLink("canonical", canonical);

    if (title)
      upsertMeta('meta[property="og:title"]', {
        property: "og:title",
        content: title,
      });
    if (description)
      upsertMeta('meta[property="og:description"]', {
        property: "og:description",
        content: description,
      });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonical,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: ogImage,
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: "british-sign.co.uk",
    });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: "en_GB",
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    if (title)
      upsertMeta('meta[name="twitter:title"]', {
        name: "twitter:title",
        content: title,
      });
    if (description)
      upsertMeta('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: description,
      });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: ogImage,
    });

    // Auto-emit Schema.org BreadcrumbList for known routes. Keeps
    // every interior page eligible for the SERP breadcrumb display
    // without each page having to opt in. We mark our injected node
    // with data-bsl-breadcrumb so subsequent navigations swap (not
    // duplicate) the previous one.
    const breadcrumb = buildBreadcrumbSchema(resolvedPath);
    let crumbScript = document.head.querySelector(
      'script[data-bsl-breadcrumb="1"]'
    );
    if (breadcrumb) {
      if (!crumbScript) {
        crumbScript = document.createElement("script");
        crumbScript.type = "application/ld+json";
        crumbScript.setAttribute("data-bsl-breadcrumb", "1");
        document.head.appendChild(crumbScript);
      }
      crumbScript.textContent = JSON.stringify(breadcrumb);
    } else if (crumbScript) {
      crumbScript.remove();
    }
  }, [title, description, path, image, type, noindex]);

  return null;
};

export default Seo;
